import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { AccessPoint, GateType, makeEmptyGate } from "../types";
import { passtrackColors } from "../theme";

interface AccessPointFormFieldsProps {
  point: AccessPoint;
  onUpdate: (patch: Partial<AccessPoint>) => void;
  hideName?: boolean;
}

const GATE_TYPES: GateType[] = ["Entrada", "Salida"];

// Timezone comes from the property (prop.tz), not per access point.
export default function AccessPointFormFields({
  point,
  onUpdate,
  hideName = false,
}: AccessPointFormFieldsProps) {
  const updateGate = (
    gateId: string,
    patch: Partial<{ name: string; type: GateType }>,
  ) => {
    onUpdate({
      gates: point.gates.map((g) =>
        g.id === gateId ? { ...g, ...patch } : g,
      ),
    });
  };

  const removeGate = (gateId: string) => {
    onUpdate({ gates: point.gates.filter((g) => g.id !== gateId) });
  };

  const addGate = () => {
    onUpdate({
      gates: [
        ...point.gates,
        makeEmptyGate(`Puerta ${point.gates.length + 1}`),
      ],
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {!hideName && (
        <TextField
          fullWidth
          size="small"
          label="Nombre"
          value={point.nombre}
          onChange={(e) => onUpdate({ nombre: e.target.value })}
          required
        />
      )}
      <TextField
        fullWidth
        size="small"
        label="Calle o Avenida"
        value={point.calleOAvenida}
        onChange={(e) => onUpdate({ calleOAvenida: e.target.value })}
      />
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Latitud"
          value={point.latitud}
          onChange={(e) => onUpdate({ latitud: e.target.value })}
        />
        <TextField
          fullWidth
          size="small"
          label="Longitud"
          value={point.longitud}
          onChange={(e) => onUpdate({ longitud: e.target.value })}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="subtitle2">Puertas</Typography>
          <Typography variant="caption" color="text.secondary">
            Configura cada puerta como entrada o salida
          </Typography>
        </Box>
        {point.gates.map((gate) => (
          <Box
            key={gate.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              background: "background.default",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              p: 1.25,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                label="Nombre"
                value={gate.name}
                onChange={(e) => updateGate(gate.id, { name: e.target.value })}
              />
              <IconButton
                size="small"
                aria-label="Eliminar puerta"
                onClick={() => removeGate(gate.id)}
                disabled={point.gates.length <= 1}
                sx={{ flexShrink: 0 }}
              >
                ×
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
              }}
            >
              {GATE_TYPES.map((type) => {
                const on = gate.type === type;
                return (
                  <Box
                    key={type}
                    component="button"
                    type="button"
                    onClick={() => updateGate(gate.id, { type })}
                    sx={{
                      py: 1,
                      px: 1.25,
                      borderRadius: 1.25,
                      cursor: "pointer",
                      font: "inherit",
                      fontSize: 13,
                      fontWeight: 600,
                      textAlign: "center",
                      background: on ? passtrackColors.cloud : "#fff",
                      color: on ? passtrackColors.blue : passtrackColors.ink,
                      border: `1.5px solid ${
                        on ? passtrackColors.blue : passtrackColors.borderSubtle
                      }`,
                    }}
                  >
                    {type}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
        <Button size="small" variant="outlined" onClick={addGate}>
          Agregar puerta
        </Button>
      </Box>
    </Box>
  );
}
